import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Link,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import CallMadeIcon from "@mui/icons-material/CallMade";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DashBoardTable from "../reuseable-components/DashBoardTable";
import {
  downloadCSV,
  downloadExcel,
  downloadPDF,
} from "../../../utils/exportUtils";
import CreateEditArticalModal from "./CreateEditArticalModal";
import ConfirmModal from "../reuseable-components/ConfirmModal";
import {
  createNewsArticle,
  deleteNewsArticle,
  updateNewsArticle,
} from "../../../apis/admin-apis/dashboard-apis/NewsTabApis";

const exportColumns = [
  { id: "title", label: "Article Title" },
  { id: "source", label: "Source" },
  { id: "category", label: "Category" },
  { id: "published", label: "Published" },
  { id: "author", label: "Author" },
  { id: "url", label: "Article URL" },
];

const formatDisplayDate = (dateValue) => {
  if (!dateValue) {
    return "";
  }

  const [year, month, day] = dateValue.split("-");

  return `${day}/${month}/${year}`;
};

const mapArticle = (article) => ({
  id: article._id,
  title: article.title || "-",
  source: article.source || "-",
  author: article.author || "-",
  category: article.category || "-",
  publicationDate: article.publicationDate || "",
  isFeatured: Boolean(article.featured),
  url: article.articleUrl || "#",
  excerpt: article.excerpt || "",
  imageUrl: article.imageUrl || "",
});

const NewsTab = ({ articlesData = [], refreshDashboard }) => {
  const [articles, setArticles] = useState(() => articlesData.map(mapArticle));
  const [sortConfig, setSortConfig] = useState({
    key: "published",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    articleId: null,
  });
  const [isSavingArticle, setIsSavingArticle] = useState(false);
  const [isDeletingArticle, setIsDeletingArticle] = useState(false);

  useEffect(() => {
    setArticles(articlesData.map(mapArticle));
  }, [articlesData]);

  const open = Boolean(anchorEl);

  // 2. Column Definitions based on image_823337.png
  const columns = [
    { id: "article", label: "Article", sortable: true },
    { id: "category", label: "Category", sortable: true },
    { id: "published", label: "Published", sortable: true },
    { id: "featured", label: "Featured", sortable: false },
    { id: "link", label: "Link", sortable: false },
    { id: "actions", label: "Actions", sortable: false },
  ];

  const sortedRows = useMemo(() => {
    const data = articles.map((article) => ({
      ...article,
      published: formatDisplayDate(article.publicationDate),
    }));

    if (sortConfig.key) {
      data.sort((a, b) => {
        let valueA = a[sortConfig.key];
        let valueB = b[sortConfig.key];

        if (sortConfig.key === "published") {
          valueA = new Date(a.publicationDate);
          valueB = new Date(b.publicationDate);
        }

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [articles, sortConfig]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenCreateModal = () => {
    setSelectedArticle(null);
    setIsArticleModalOpen(true);
  };

  const handleOpenEditModal = (article) => {
    setSelectedArticle(article);
    setIsArticleModalOpen(true);
  };

  const handleCloseArticleModal = () => {
    setIsArticleModalOpen(false);
    setSelectedArticle(null);
  };

  const handleSaveArticle = async (articleValues) => {
    setIsSavingArticle(true);

    try {
      const payload = {
        title: articleValues.title,
        source: articleValues.source,
        publicationDate: articleValues.publicationDate,
        articleUrl: articleValues.url,
        category: articleValues.category,
        author: articleValues.author,
        excerpt: articleValues.excerpt,
        imageUrl: articleValues.imageUrl,
        featured: Boolean(articleValues.isFeatured),
      };

      if (selectedArticle?.id) {
        const response = await updateNewsArticle(selectedArticle.id, payload);
        const updatedArticle = response?.article;

        if (updatedArticle) {
          const mappedArticle = mapArticle(updatedArticle);
          setArticles((prev) =>
            prev.map((article) =>
              article.id === selectedArticle.id ? mappedArticle : article,
            ),
          );
          if (refreshDashboard) refreshDashboard();
        }
      } else {
        const response = await createNewsArticle(payload);
        const createdArticle = response?.article;

        if (createdArticle) {
          setArticles((prev) => [mapArticle(createdArticle), ...prev]);
          if (refreshDashboard) refreshDashboard();
        }
      }

      handleCloseArticleModal();
    } catch (error) {
      console.error("Failed to save news article:", error);
    } finally {
      setIsSavingArticle(false);
    }
  };

  const handleOpenDeleteConfirm = (articleId) => {
    setConfirmState({
      open: true,
      articleId,
    });
  };

  const handleCloseConfirm = () => {
    setConfirmState({
      open: false,
      articleId: null,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmState.articleId) {
      handleCloseConfirm();
      return;
    }

    setIsDeletingArticle(true);

    try {
      const response = await deleteNewsArticle(confirmState.articleId);

      if (response?.success) {
        setArticles((prev) =>
          prev.filter((article) => article.id !== confirmState.articleId),
        );
        if (refreshDashboard) refreshDashboard();
      }
    } catch (error) {
      console.error("Failed to delete news article:", error);
    } finally {
      setIsDeletingArticle(false);
      handleCloseConfirm();
    }
  };

  // 4. Transform Data for the Table Component
  const rows = paginatedRows.map((row) => ({
    ...row,
    // Article column with title and sub-info
    article: (
      <Box sx={{ py: 0.5 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: "#0F1E3D",
            lineHeight: 1.2,
          }}
        >
          {row.title}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "#999", mt: 0.5 }}>
          {row.source} · {row.author}
        </Typography>
      </Box>
    ),
    // Featured Badge with Star Icon
    featured: row.isFeatured ? (
      <Chip
        icon={<StarIcon style={{ fontSize: "14px", color: "#B38B00" }} />}
        label="Featured"
        size="small"
        sx={{
          bgcolor: "#FFF4D1",
          color: "#B38B00",
          fontSize: "11px",
          fontWeight: 700,
          borderRadius: "4px",
          height: "24px",
          "& .MuiChip-icon": { marginLeft: "4px" },
        }}
      />
    ) : (
      "-"
    ),
    // Link column
    link: (
      <Link
        href={row.url}
        underline="hover"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "#D4A017",
          fontSize: "13px",
          fontWeight: 600,
        }}
      >
        View <CallMadeIcon sx={{ fontSize: "14px" }} />
      </Link>
    ),
    // Actions Group (Edit / Archive)
    actions: (
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenEditModal(row)}
          sx={{
            bgcolor: "#D4A017",
            textTransform: "none",
            fontSize: "11px",
            minWidth: "auto",
            height: "28px",
            "&:hover": { bgcolor: "#B8860B" },
          }}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenDeleteConfirm(row.id)}
          sx={{
            bgcolor: "#FFEAEA",
            color: "#D32F2F",
            textTransform: "none",
            fontSize: "11px",
            minWidth: "auto",
            height: "28px",
            boxShadow: "none",
            "&:hover": { bgcolor: "#FFDADA" },
          }}
        >
          Delete
        </Button>
      </Stack>
    ),
  }));

  return (
    <Box
      className="news-tab-container"
      sx={{
        p: {
          xs: 1,
          md: 3,
        },
        bgcolor: "#fff",
      }}
    >
      {/* Header Section */}
      <Box
        className="news-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#0F1E3D",
            fontSize: {
              xs: "16px",
              md: "1.25rem",
            },
          }}
        >
          News Articles
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleExportClick}
            sx={{
              bgcolor: "#D4A017",
              "&:hover": { bgcolor: "#B8860B" },
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
            }}
          >
            Export
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                const exportData = sortedRows.map((row) => ({
                  title: row.title,
                  source: row.source,
                  category: row.category,
                  published: row.published,
                  author: row.author,
                  url: row.url,
                }));
                downloadCSV({
                  data: exportData,
                  fileName: "news-articles",
                });
                handleClose();
              }}
            >
              Download CSV
            </MenuItem>

            <MenuItem
              onClick={() => {
                const exportData = sortedRows.map((row) => ({
                  title: row.title,
                  source: row.source,
                  category: row.category,
                  published: row.published,
                  author: row.author,
                  url: row.url,
                }));
                downloadExcel({
                  data: exportData,
                  fileName: "news-articles",
                  sheetName: "News Articles",
                });
                handleClose();
              }}
            >
              Download Excel
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadPDF({
                  data: sortedRows,
                  fileName: "news-articles",
                  title: "News Articles",
                  columns: exportColumns,
                });
                handleClose();
              }}
            >
              Download PDF
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            sx={{
              bgcolor: "#D4A017",
              "&:hover": { bgcolor: "#B8860B" },
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
            }}
          >
            New Article
          </Button>
        </Box>
      </Box>

      {/* Decorative Border */}
      <Box sx={{ borderBottom: "1px solid #EAE2D1", mb: 4 }} />

      {/* Table Section */}
      <Box className="news-table-wrapper">
        <DashBoardTable
          columns={columns}
          rows={rows}
          sortConfig={sortConfig}
          onSort={(key) =>
            setSortConfig({
              key,
              direction:
                sortConfig.key === key && sortConfig.direction === "asc"
                  ? "desc"
                  : "asc",
            })
          }
          page={page}
          totalPages={Math.ceil(sortedRows.length / rowsPerPage)}
          onPageChange={setPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(v) => {
            setRowsPerPage(v);
            setPage(1);
          }}
          totalResults={sortedRows.length}
        />
      </Box>

      <CreateEditArticalModal
        open={isArticleModalOpen}
        handleClose={handleCloseArticleModal}
        articleData={selectedArticle}
        onSave={handleSaveArticle}
        isSaving={isSavingArticle}
      />
      <ConfirmModal
        open={confirmState.open}
        handleClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        confirmLabel="Delete"
        loading={isDeletingArticle}
      />
    </Box>
  );
};

export default NewsTab;
